import { useEffect, useState } from 'react';
import Input from '../../components/Input';
import './create-user.css';
import { useNavigate } from 'react-router-dom';
import CloseButton from '../../components/CloseButton';
import RadioInput from '../../components/RadioInput';
import { create, get, post } from '../../services/api';
import { useUI } from '../../UIContext';
import { handleErr } from '../../utils/functions';
import PhoneInput from '../../components/PhoneInput'
import Permissions from '../../components/Permissions';

function CreateUser() {
    const individualPersonType = 'individual';
    const companyPersonType = 'company';
    const modularPlanType = 'modular';
    const hubPlanType = 'hub';
    const userTab = 0;
    const permissionTab = 1;

    const navigate = useNavigate();

    const { setMessage, setLoading } = useUI();
    const [personType,  setPersonType] = useState(individualPersonType);
    const [name, setName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [phone, setPhone] = useState(undefined);
    const [submitAttempt, setSubmitAttempt] = useState(false);
    const [tabActive, setTabActive] = useState(userTab);
    const [permissions, setPermissions] = useState([]);
    const [planType, setPlanType] = useState(modularPlanType);
    const [hasSavedProcess, setHasSavedProcess] = useState(false);

    useEffect(() => {
        async function getPermissionsList() {
            setLoading(true);

            await get('/module/class/', true, setMessage,
                (response) => {
                    setPermissions(
                        response.data.map(el => {
                            return {
                                id: el.module.id,
                                name: el.module.name,
                                supplementary: el.module.supplementary,
                                checked: false
                            }
                        })
                    );
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar a lista de permissões: ${err}`);
                },
                () => {
                    setLoading(false);
                }
            )
        }

        getPermissionsList();
    }, [setLoading, setMessage, setPermissions]);

    function onChangePersonType(e) {
        setPersonType(e.target.value);
    }

    function isCompanyPerson() {
        return personType === companyPersonType;
    }

    function isNameValid() {
        return !!name && name.length >= 2 && name.length <= 56;
    }

    function isLastNameValid() {
        return !!lastName && lastName.length >= 2 && lastName.length <= 200;
    }

    function isEmailValid() {
        return !!email && email.length >= 4 && email.length <= 256;
    }

    function isDocumentNumberValid() {
        return !!documentNumber && documentNumber.length <= 60;
    }

    function clearScreen() {
        setSubmitAttempt(false);
        setName('');
        setLastName('');
        setEmail('');
        setDocumentNumber('');
        setPhone('');
        setPlanType(modularPlanType);
        setPermissions(prev =>
            prev.map(p => {
                return {
                    ...p,
                    checked: false
                };
            })
        );
        setTabActive(userTab);
    }

    async function onClickSave(e) {
        function showSuccess() {
            setHasSavedProcess(true);
            setMessage({ type: 'success', text: 'O usuário foi cadastrado.' });
        }


        function isSaveValid() {
            setSubmitAttempt(true);

            if (isCompanyPerson()) {
                return !!name && !!email && !!documentNumber;
            }

            return !!name && !!lastName && !!email && !!documentNumber;
        }

        e.preventDefault();

        if (!isSaveValid())
            return;

        setLoading(true);

        const data = {
            name,
            lastName: isCompanyPerson() ? null : lastName,
            email,
            person: {
                type: personType,
                documentNumber,
                phone
            }
        };

        await create('user', data, true, setMessage,
            async (response) => {
                await post('/plan/configure', {
                    type: planType,
                    userId: response.data.id
                }, true, setMessage,
                    async () => {
                        if (planType === hubPlanType) {
                            clearScreen();
                            showSuccess();
                            return;
                        }

                        if (permissions.length === 0) {
                            clearScreen();
                            showSuccess();
                            return;
                        }

                        const modulesChecked = permissions.filter(p => p.checked);

                        const data = {
                            modulesIds: modulesChecked.map(p => p.id)
                        };

                        await post(`permission/user/${response.data.id}`, data, true, setMessage,
                            () => {
                                clearScreen();
                                showSuccess();
                            },
                            (err) => {
                                handleErr(setMessage, `Não foi possível incluir as permissões do usuário: ${err}`);
                            }
                        )
                    },
                    (err) => {
                        handleErr(setMessage, `Não foi possível configurar o plano do usuário: ${err}`);
                    }
                )
            },
            (err) => {
                handleErr(setMessage, `Não foi possível cadastrar o usuário: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }

    function onChangePlanType(e) {
        setPlanType(e.target.value);
    }

    function onClickUserTab(e) {
        e.preventDefault();

        setTabActive(userTab);
    }

    function onClickPermissionTab(e) {
        e.preventDefault();

        setTabActive(permissionTab);
    }

    function onChangeModulePermission(permission) {
        setPermissions(prev =>
            prev.map(p => {
                if (p !== permission) return p;

                const newChecked = !p.checked;

                return {
                    ...p,
                    checked: newChecked
                };
            })
        );
    }

    return (
        <form className='default-form'>
            <header className='container-header'>
                <h1>Novo Usuário</h1>

                <CloseButton
                    onClick={() => navigate('/users')}
                    shouldConfirm={!hasSavedProcess}
                />
            </header>

            <div className="tab-list">
                <button
                    type='button'
                    onClick={onClickUserTab}
                    className={`tab-button ${tabActive === userTab ? 'active' : ''}`}>
                    Usuários
                </button>
                <button
                    type='button'
                    onClick={onClickPermissionTab}
                    className={`tab-button ${tabActive === permissionTab ? 'active' : ''}`}>
                    Planos
                </button>
            </div>

            <div className="tab-content">
                {tabActive === userTab && (
                    <div id='user-tab'>
                        <div className='person-type-container'>
                            <RadioInput
                                className='person-type-input'
                                label='Pessoa Física'
                                name='person-type'
                                value={individualPersonType}
                                checked={personType === individualPersonType}
                                onChange={onChangePersonType}
                            />

                            <RadioInput
                                className='person-type-input'
                                label='Empresa'
                                name='person-type'
                                value={companyPersonType}
                                checked={personType === companyPersonType}
                                onChange={onChangePersonType}
                            />
                        </div>

                        <div className={`container-user-group-data ${isCompanyPerson() ? 'single-field' : ''}`}>
                            <span className='first-input-container'>
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onValidate={isNameValid}
                                    placeholder={isCompanyPerson() ? 'Denominação' : 'Nome'}
                                    shouldValidate={submitAttempt}
                                    maxLength={56}
                                    warningMsg='O nome deve conter ao menos 3 caracteres.'
                                />
                            </span>

                            {!isCompanyPerson() && (
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    onValidate={isLastNameValid}
                                    placeholder='Sobrenome'
                                    shouldValidate={submitAttempt}
                                    maxLength={200}
                                    warningMsg='O sobrenome deve conter ao menos 3 caracteres.'
                                />
                            )}
                        </div>

                        <Input
                            className='commom-input glass-effect-15'
                            type='text'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onValidate={isEmailValid}
                            placeholder='E-mail'
                            shouldValidate={submitAttempt}
                            maxLength={256}
                            warningMsg='O e-mail deve ser informado.'
                        />

                        <div className='container-user-group-data'>
                            <span className='first-input-container'>
                                <Input
                                    className='commom-input glass-effect-15'
                                    type='text'
                                    value={documentNumber}
                                    onChange={(e) => setDocumentNumber(e.target.value)}
                                    onValidate={isDocumentNumberValid}
                                    placeholder='Número de documento'
                                    shouldValidate={submitAttempt}
                                    maxLength={60}
                                    warningMsg='O número de documento deve ser informado.'
                                />
                            </span>

                            <span>
                                <PhoneInput
                                    className='commom-input glass-effect-15'
                                    placeholder='Contato Telefónico'
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                            </span>
                        </div>
                    </div>
                )}

                {tabActive === permissionTab && (
                    <div className='permissions-tab'>
                        <div className='plan-type-container'>
                            <RadioInput
                                className='person-type-input'
                                label='Plano Modular'
                                name='plan-type'
                                value={modularPlanType}
                                checked={planType === modularPlanType}
                                onChange={onChangePlanType}
                            />

                            <RadioInput
                                className='person-type-input'
                                label='Plano HUB'
                                name='plan-type'
                                value={hubPlanType}
                                checked={planType === hubPlanType}
                                onChange={onChangePlanType}
                            />
                        </div>

                        {planType === hubPlanType ? (
                            <p className='hub-plan-text'>Neste plano o usuário tem acesso à todos os módulos</p>
                        ) : (
                            <Permissions
                                permissions={permissions}
                                onChangeModulePermission={onChangeModulePermission}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className='container-save-form'>
                <button
                    type='submit'
                    onClick={onClickSave}
                    className='blue-button zoom-effect'>
                    Salvar
                </button>
            </div>
        </form>
    );
}

export default CreateUser;